import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, TextField, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Chip, Tooltip, FormControl, InputLabel, Select, MenuItem, ListItem, ListItemText, ListItemSecondaryAction, FormControlLabel, Switch, Tab, Tabs, Alert } from '@mui/material';
import { Add, Visibility, VisibilityOff, Refresh, Delete, Block, CheckCircle, CopyAllSharp, Settings, BarChart, ArrowUpward, Edit, ErrorOutline, LockOpen, InfoOutlined } from '@mui/icons-material';
import Input from '../Input';
import axios from 'axios';
import ApiKeyUsage from './ApiKeyUsage';
import ApiKeyLimits from './ApiKeyLimits';
import { useAuth } from '../../context/AuthContext';
import ServiceTierChangeModal from './ServiceTierChangeModal';

// Component to display tier-specific error messages with upgrade recommendations
const TierErrorMessage = ({ error, onUpgradeClick }) => {
    if (!error) return null;
    
    // Check if the error is a tier limit error
    const isTierLimitError = error.includes('limit') || error.includes('tier');
    
    if (!isTierLimitError) {
        return <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>;
    }

    // Extract tier name from the error message
    let tierName = 'your current';
    if (error.includes('free tier')) tierName = 'free';
    if (error.includes('professional tier')) tierName = 'professional';
    if (error.includes('enterprise tier')) tierName = 'enterprise';
    
    // Define upgrade message based on tier
    let upgradeMessage = '';
    if (tierName === 'free') {
        upgradeMessage = 'Upgrade to the Professional tier for more API keys and higher usage limits.';
    } else if (tierName === 'professional') {
        upgradeMessage = 'Upgrade to the Enterprise tier for maximum API keys and usage limits.';
    } else if (tierName === 'enterprise') {
        upgradeMessage = 'Please contact support for custom API key allocation.';
    }
    
    return (
        <Alert 
            severity="warning" 
            icon={<ErrorOutline />}
            sx={{ mb: 2 }}
            action={
                tierName !== 'enterprise' ? (
                    <Button 
                        color="warning" 
                        size="small" 
                        startIcon={<ArrowUpward />}
                        onClick={onUpgradeClick}
                    >
                        Upgrade
                    </Button>
                ) : (
                    <Button 
                        color="warning" 
                        size="small" 
                        startIcon={<LockOpen />}
                        onClick={() => window.location.href = 'mailto:support@eudr-api.com'}
                    >
                        Contact Support
                    </Button>
                )
            }
        >
            <Typography variant="body2" fontWeight="medium">
                {error}
            </Typography>
            <Typography variant="body2">
                {upgradeMessage}
            </Typography>
        </Alert>
    );
};

function APIKeysPanel({ onNotification }) {
    const { user, fetchUserProfile } = useAuth();
    const [apiKeys, setApiKeys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [newKeyName, setNewKeyName] = useState('');
    const [newKeyWebClientId, setNewKeyWebClientId] = useState('eudr-test');
    const [showKeys, setShowKeys] = useState({});
    const [hoveredRow, setHoveredRow] = useState(null);
    const [webClientDialog, setWebClientDialog] = useState({ open: false, keyId: null, webClientId: '' });
    const [renameDialog, setRenameDialog] = useState({ open: false, keyId: null, name: '' });
    const [selectedKeyForUsage, setSelectedKeyForUsage] = useState(null);
    const [usageDialogOpen, setUsageDialogOpen] = useState(false);
    const [showLimits, setShowLimits] = useState(false);
    const [tierChangeDialog, setTierChangeDialog] = useState({ open: false, newTier: null });
    const [limitsRefreshTrigger, setLimitsRefreshTrigger] = useState(0);

    // Load API keys on component mount
    useEffect(() => {
        fetchApiKeys();
    }, []);

    // Set default API key name when no keys exist
    useEffect(() => {
        if (apiKeys.length === 0 && !loading && !newKeyName) {
            setNewKeyName('Default API Key');
        }
    }, [apiKeys, loading, newKeyName]);

    // Fetch API keys from server
    const fetchApiKeys = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/auth/api-keys');
            
            // Ensure all keys have proper fields and sort by creation date (newest first)
            const sortedKeys = response.data
                .map(key => ({
                    ...key,
                    // If for some reason createdAt is missing, provide a fallback
                    createdAt: key.createdAt || new Date().toISOString()
                }))
                .sort((a, b) => {
                    return new Date(b.createdAt) - new Date(a.createdAt);
                });
            
            setApiKeys(sortedKeys);
            setError(null);
        } catch (err) {
            setError('Failed to load API keys');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Generate a new API key
    const handleCreateApiKey = async () => {
        if (!newKeyName.trim()) return;

        // Check if name already exists
        const nameExists = apiKeys.some(key =>
            key.name.toLowerCase() === newKeyName.trim().toLowerCase()
        );

        if (nameExists) {
            setError('An API key with this name already exists. Please use a unique name.');
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post('/api/auth/api-keys', { 
                name: newKeyName,
                webClientId: newKeyWebClientId
            });
            
            // Ensure the response data includes the name field
            const newKey = {
                ...response.data,
                name: newKeyName, // Ensure name is set properly
                createdAt: new Date().toISOString(), // Ensure createdAt is set
                isActive: true // Ensure new keys are active by default
            };

            setShowKeys(prev => ({
                ...prev,
                [newKey.id]: true
            }));
            
            // Add to beginning of the array, not the end
            setApiKeys([newKey, ...apiKeys]);
            
            // Trigger limits refresh
            refreshLimits();
            
            onNotification(
                'API Key Created',
                'Your new API key has been successfully created.',
                'success'
            );
            setNewKeyName('');
            setNewKeyWebClientId('eudr-test');
            setError(null); // Clear any previous errors
        } catch (err) {
            handleApiKeyError(err, 'create');
        } finally {
            setLoading(false);
        }
    };

    // Handle tier upgrade button click - now using ServiceTierChangeModal
    const handleUpgradeClick = () => {
        const currentTier = user?.serviceTier || 'free';
        const nextTier = currentTier === 'free' ? 'professional' : 'enterprise';
        
        // Only proceed if we're not already at enterprise level
        if (currentTier !== 'enterprise') {
            setTierChangeDialog({
                open: true,
                newTier: nextTier
            });
        } else {
            onNotification(
                'Enterprise Tier',
                'You are already on the Enterprise tier. Please contact support for custom requirements.',
                'info'
            );
        }
    };
    
    // Handle successful tier change
    const handleTierChangeSuccess = async () => {
        setTierChangeDialog({ open: false, newTier: null });
        
        onNotification(
            'Service Tier Updated',
            'Your service tier has been updated successfully.',
            'success'
        );
        
        // Refresh user data to get updated tier
        try {
            await fetchUserProfile();
            // Also refresh API key limits and data
            fetchApiKeys();
        } catch (err) {
            console.error('Error refreshing profile after tier change:', err);
        }
    };
    
    // Handle tier change cancellation
    const handleTierChangeCancel = () => {
        setTierChangeDialog({ open: false, newTier: null });
    };

    // Enhanced error handling for API key operations
    const handleApiKeyError = (error, operation) => {
        const errorMsg = error.response?.data?.message || `Failed to ${operation} API key`;
        setError(errorMsg);
        
        // Create custom notification based on error type
        if (errorMsg.includes('limit') || errorMsg.includes('tier')) {
            // For tier limit errors
            onNotification(
                'Tier Limit Reached',
                errorMsg,
                'warning'
            );
        } else {
            // For other errors
            onNotification(
                `Failed to ${operation.charAt(0).toUpperCase() + operation.slice(1)} API Key`,
                errorMsg,
                'error'
            );
        }
    };

    // Regenerate an API key with enhanced error handling
    const handleRegenerateKey = async (id) => {
        setLoading(true);
        try {
            const response = await axios.put(`/api/auth/api-keys/${id}/regenerate`);
            
            // Find the existing key to preserve other properties
            const existingKey = apiKeys.find(key => key.id === id);
            
            // Create updated key with all properties preserved
            const updatedKey = {
                ...existingKey,
                key: response.data.key,
                lastUsed: response.data.lastUsed || existingKey.lastUsed,
                isActive: response.data.isActive
            };
            
            setShowKeys(prev => ({
                ...prev,
                [id]: true
            }));
            
            // Update the key in the array while maintaining sort order
            setApiKeys(apiKeys.map(key => key.id === id ? updatedKey : key));
            
            onNotification(
                'API Key Regenerated',
                'A new API key has been generated. Please save it securely as it won\'t be shown again.',
                'success'
            );
        } catch (err) {
            handleApiKeyError(err, 'regenerate');
        } finally {
            setLoading(false);
        }
    };

    // Helper function to update API key status with enhanced error handling
    const handleToggleStatus = async (id, currentStatus) => {
        setLoading(true);
        try {
            await axios.put(`/api/auth/api-keys/${id}/toggle`);
            
            setApiKeys(apiKeys.map(key => key.id === id ? { 
                ...key, 
                isActive: !currentStatus 
            } : key));
            
            onNotification(
                'API Key Status Updated',
                `API key has been ${!currentStatus ? 'activated' : 'deactivated'}.`,
                'success'
            );
            
            // Clear any previous errors on success
            setError(null);
        } catch (err) {
            handleApiKeyError(err, currentStatus ? 'deactivate' : 'activate');
        } finally {
            setLoading(false);
        }
    };

    // Delete an API key with enhanced error handling
    const handleDeleteKey = async (id) => {
        setLoading(true);
        try {
            await axios.delete(`/api/auth/api-keys/${id}`);
            setApiKeys(apiKeys.filter(key => key.id !== id));
            onNotification(
                'API Key Deleted',
                'The API key has been successfully deleted.',
                'success'
            );
            
            // Trigger limits refresh
            refreshLimits();
            
            // Clear any previous errors on success
            setError(null);
        } catch (err) {
            handleApiKeyError(err, 'delete');
        } finally {
            setLoading(false);
        }
    };

    // Toggle visibility of an API key
    const showKey = (id) => {
        // Toggle the visibility state for this key
        setShowKeys(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
        
        // Copy to clipboard when showing the key
        const key = apiKeys.find(key => key.id === id);
        if (!showKeys[id] && key?.key) {
            navigator.clipboard.writeText(key.key)
                .then(() => {
                    onNotification(
                        'Copied to Clipboard',
                        'API key has been copied to clipboard',
                        'info'
                    );
                })
                .catch(err => {
                    console.error('Failed to copy API key:', err);
                });
        }
    };

    // Open web client ID dialog
    const openWebClientDialog = (key) => {
        setWebClientDialog({
            open: true,
            keyId: key.id,
            webClientId: key.webClientId || 'eudr-test'
        });
    };

    // Close web client ID dialog
    const closeWebClientDialog = () => {
        setWebClientDialog({ open: false, keyId: null, webClientId: '' });
    };

    // Update web client ID
    const handleUpdateWebClientId = async () => {
        try {
            setLoading(true);
            await axios.put(`/api/auth/api-keys/${webClientDialog.keyId}/web-client-id`, {
                webClientId: webClientDialog.webClientId
            });
            await fetchApiKeys();
            closeWebClientDialog();
        } catch (err) {
            setError('Failed to update Web Client ID');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Open rename dialog
    const openRenameDialog = (key) => {
        setRenameDialog({
            open: true,
            keyId: key.id,
            name: key.name
        });
    };

    // Close rename dialog
    const closeRenameDialog = () => {
        setRenameDialog({ open: false, keyId: null, name: '' });
    };

    // Update API key name
    const handleUpdateKeyName = async () => {
        if (!renameDialog.name.trim()) {
            setError('API key name cannot be empty');
            return;
        }

        try {
            setLoading(true);
            const response = await axios.put(`/api/auth/api-keys/${renameDialog.keyId}/name`, {
                name: renameDialog.name
            });
            
            // Update the key in the array
            setApiKeys(apiKeys.map(key => 
                key.id === renameDialog.keyId 
                    ? { ...key, name: response.data.data.name } 
                    : key
            ));
            
            closeRenameDialog();
            
            onNotification(
                'API Key Renamed',
                'The API key has been successfully renamed.',
                'success'
            );
        } catch (err) {
            const errorMsg = err.response?.data?.message || 'Failed to rename API key';
            setError(errorMsg);
            
            onNotification(
                'Failed to Rename API Key',
                errorMsg,
                'error'
            );
        } finally {
            setLoading(false);
        }
    };

    // Copy key to clipboard
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
    };

    // Open usage statistics dialog for a key
    const openUsageDialog = (key) => {
        setSelectedKeyForUsage(key);
        setUsageDialogOpen(true);
    };

    // Close usage statistics dialog
    const closeUsageDialog = () => {
        setUsageDialogOpen(false);
        setSelectedKeyForUsage(null);
    };

    // Toggle showing limits section
    const toggleLimits = () => {
        setShowLimits(!showLimits);
    };

    // Add this function to force a refresh of the limits
    const refreshLimits = () => {
        setLimitsRefreshTrigger(prev => prev + 1);
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                API Keys
            </Typography>

            <Typography variant="body2" color="text.secondary">
                Use these API keys to authenticate your requests. Keep them secure and never share them publicly.
            </Typography>

            {/* Tier-specific error messages with upgrade recommendations */}
            <TierErrorMessage error={error} onUpgradeClick={handleUpgradeClick} />

            {/* Tier Limits Toggle Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                    size="small"
                    color="primary"
                    onClick={toggleLimits}
                    startIcon={showLimits ? null : <ArrowUpward />}
                >
                    {showLimits ? 'Hide Tier Limits' : 'Show Tier Limits'}
                </Button>
            </Box>

            {/* Tier Limits Section */}
            {showLimits && (
                <ApiKeyLimits 
                    onUpgradeClick={handleUpgradeClick} 
                    refreshTrigger={limitsRefreshTrigger} 
                />
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', mt: 3, mb: 3 }}>
                {/* API Key Creation Form */}
                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, mb: 2 }}>
                    <Tooltip title="A descriptive name to help you identify this API key (e.g., 'Production Key', 'Test Key', etc.)" placement="top" arrow>
                        <div style={{ flexGrow: 1 }}>
                            <Input
                                label="API Key Name"
                                variant="outlined"
                                size="small"
                                value={newKeyName}
                                onChange={(e) => setNewKeyName(e.target.value)}
                                placeholder="Enter a descriptive name"
                                fullWidth
                            />
                        </div>
                    </Tooltip>
                    <Tooltip title="Select which EUDR environment this key will access" placement="top" arrow>
                        <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
                            <InputLabel id="new-key-web-client-id-label">Web Client ID</InputLabel>
                            <Select
                                labelId="new-key-web-client-id-label"
                                id="new-key-web-client-id"
                                value={newKeyWebClientId}
                                onChange={(e) => setNewKeyWebClientId(e.target.value)}
                                label="Web Client ID"
                                sx={{ backgroundColor: 'background.paper' }}
                            >
                                <MenuItem value="eudr-test">Acceptance (test) system</MenuItem>
                                <MenuItem value="eudr">Production system</MenuItem>
                            </Select>
                        </FormControl>
                    </Tooltip>
                    <Tooltip title="Generate a new API key with these settings" placement="top" arrow>
                        <span>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<Add />}
                                onClick={handleCreateApiKey}
                                disabled={loading || !newKeyName.trim()}
                            >
                                Create Key
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
                
                {/* Tier reminder below creation form */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <InfoOutlined color="info" fontSize="small" />
                    <Typography variant="body2" color="text.secondary">
                        Need more API keys? {' '}
                        <Button 
                            color="primary" 
                            size="small" 
                            onClick={toggleLimits}
                            sx={{ minWidth: 'auto', p: '2px 8px', textTransform: 'none' }}
                        >
                            {showLimits ? 'Hide tier limits' : 'Show tier limits'}
                        </Button>
                        {' '} or {' '}
                        <Button 
                            color="primary" 
                            size="small" 
                            onClick={handleUpgradeClick}
                            sx={{ minWidth: 'auto', p: '2px 8px', textTransform: 'none' }}
                        >
                            upgrade your plan
                        </Button>
                    </Typography>
                </Box>
            </Box>

            {loading && apiKeys.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress />
                </Box>
            ) : apiKeys.length === 0 ? (
                // First-time user experience
                <Box sx={{ textAlign: 'center', my: 4, p: 3, border: '1px dashed', borderColor: 'primary.main', borderRadius: 2 }}>
                    <Typography variant="h6" color="primary" gutterBottom>
                        Create Your First API Key
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        You haven't created any API keys yet. API keys are required to authenticate your requests to the EUDR API.
                    </Typography>
                    <Typography variant="body2" paragraph>
                        We've suggested a default name for your first key above. You can customize it if needed.
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<Add />}
                        onClick={handleCreateApiKey}
                        disabled={loading || !newKeyName.trim()}
                        size="large"
                        sx={{ mt: 2 }}
                    >
                        Create Your First API Key
                    </Button>
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>API Key</TableCell>
                                <TableCell>Web Client ID</TableCell>
                                <TableCell>Created</TableCell>
                                <TableCell>Last Used</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="center">Usage</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {apiKeys.map((key) => (
                                <TableRow
                                    key={key.id}
                                    onMouseEnter={() => setHoveredRow(key.id)}
                                    onMouseLeave={() => setHoveredRow(null)}
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                        },
                                        position: 'relative'
                                    }}
                                >
                                    <TableCell sx={{ minWidth: '100px' }}>{key.name || 'Unnamed Key'}</TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" sx={{ fontFamily: 'Courier New' }}>
                                                {showKeys[key.id] ? key.key : '••••••••••••••••••••••••••••••'}
                                            </Typography>
                                            <Tooltip title="Copy to clipboard">
                                                <IconButton size="small" onClick={() => copyToClipboard(key.key)}>
                                                    <CopyAllSharp fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <IconButton size="small" onClick={() => showKey(key.id)}>
                                                {showKeys[key.id] ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={key.webClientId === 'eudr-test' ? 'Acceptance (test)' : 'Production'} 
                                            color={key.webClientId === 'eudr-test' ? 'info' : 'warning'}
                                            size="small"
                                            onClick={() => openWebClientDialog(key)}
                                            sx={{ cursor: 'pointer' }}
                                        />
                                    </TableCell>
                                    <TableCell>{new Date(key.createdAt).toLocaleDateString()}</TableCell>
                                    <TableCell>{key.lastUsed ? new Date(key.lastUsed).toLocaleDateString() : 'Never'}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={key.isActive ? "Active" : "Revoked"}
                                            color={key.isActive ? "success" : "error"}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="View Usage Statistics">
                                            <IconButton 
                                                color="primary" 
                                                size="small"
                                                onClick={() => openUsageDialog(key)}
                                            >
                                                <BarChart fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                    {hoveredRow === key.id && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                left: '10px',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                display: 'flex',
                                                backgroundColor: 'background.paper',
                                                borderRadius: '4px',
                                                boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                                                padding: '4px',
                                                zIndex: 1
                                            }}
                                        >
                                            <Tooltip title="Rename API Key">
                                                <IconButton onClick={() => openRenameDialog(key)} color="info" size="small">
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Change Web Client ID">
                                                <IconButton onClick={() => openWebClientDialog(key)} color="info" size="small">
                                                    <Settings fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Regenerate key">
                                                <IconButton onClick={() => handleRegenerateKey(key.id)} color="primary" size="small">
                                                    <Refresh fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={key.isActive ? "Revoke" : "Activate"}>
                                                <IconButton onClick={() => handleToggleStatus(key.id, key.isActive)} color={key.isActive ? "warning" : "success"} size="small">
                                                    {key.isActive ? <Block fontSize="small" /> : <CheckCircle fontSize="small" />}
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton onClick={() => handleDeleteKey(key.id)} color="error" size="small">
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Web Client ID Dialog */}
            <Dialog open={webClientDialog.open} onClose={closeWebClientDialog}>
                <DialogTitle>Change Web Client ID</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        Select the Web Client ID for this API key. Use the test system for development and the production system for live operations.
                    </Typography>
                    <FormControl fullWidth margin="normal">
                        <InputLabel id="web-client-id-dialog-label">Web Client ID</InputLabel>
                        <Select
                            labelId="web-client-id-dialog-label"
                            value={webClientDialog.webClientId}
                            onChange={(e) => setWebClientDialog(prev => ({ ...prev, webClientId: e.target.value }))}
                            label="Web Client ID"
                        >
                            <MenuItem value="eudr-test">Acceptance (test) system</MenuItem>
                            <MenuItem value="eudr">Production system</MenuItem>
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeWebClientDialog}>Cancel</Button>
                    <Button onClick={handleUpdateWebClientId} color="primary" variant="contained">
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Rename API Key Dialog */}
            <Dialog open={renameDialog.open} onClose={closeRenameDialog}>
                <DialogTitle>Rename API Key</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        Enter a new name for your API key. Choose a name that helps you identify the purpose or usage context of this key.
                    </Typography>
                    <TextField
                        autoFocus
                        margin="normal"
                        label="API Key Name"
                        type="text"
                        fullWidth
                        value={renameDialog.name}
                        onChange={(e) => setRenameDialog(prev => ({ ...prev, name: e.target.value }))}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeRenameDialog}>Cancel</Button>
                    <Button 
                        onClick={handleUpdateKeyName} 
                        color="primary" 
                        variant="contained"
                        disabled={!renameDialog.name.trim()}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Usage Statistics Dialog */}
            <Dialog 
                open={usageDialogOpen} 
                onClose={closeUsageDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    API Key Usage Statistics: <Typography display="inline" fontSize="0.9em" letterSpacing="2px" textTransform="uppercase" color="primary" fontWeight="bold">{selectedKeyForUsage?.name}</Typography> 
                </DialogTitle>
                <DialogContent>
                    {selectedKeyForUsage && (
                        <ApiKeyUsage 
                            apiKeyId={selectedKeyForUsage.id} 
                            apiKeyName={selectedKeyForUsage.name}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeUsageDialog} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Tier Change Dialog */}
            <ServiceTierChangeModal
                open={tierChangeDialog.open}
                onClose={handleTierChangeCancel}
                currentTier={user?.serviceTier || 'free'}
                newTier={tierChangeDialog.newTier}
                onSuccess={handleTierChangeSuccess}
            />
        </Box>
    );
}

export default APIKeysPanel; 